import type {ReactElement} from 'react';
import {
  getIconsByFooterType,
  getOptionsTextByFooterType,
  SocialMediaMobileFooterType,
} from '@Components/social-media-wizard/components/social-media-mobile-footer/social-media-mobile-footer.types';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {
  useAccountIdsToPublishTo,
  useNextButtonDisabled,
  useNextButtonIcon,
  useNextButtonText,
  useSocialMediaWizardPost,
  useWizardStep,
} from '@Components/social-media/social-media.hooks';
import {SocialMediaWizardStep, WizardMobileNavView} from '@Components/social-media-wizard/social-media-wizard.types';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import type {SocialMediaWizardMobileFooterModalProps} from '@Modals/social-media-wizard-mobile-footer-modal';
import {openSocialMediaMobileFooterModal} from '@Modals/social-media-wizard-mobile-footer-modal';
import {formatLocalDate} from '@Utils/date.util';
import {Text, TextSize} from '@Components/text';
import {setWizardMobileNavView, updatePost, updatePublishingAction, updateTriggerNext} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {noop} from '@Utils/general.util';
import {SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import {PostState} from '@Components/social-media/post.vo';
import {
  useAreCaptionsBeingAutoGenerated,
  useHideNextButton,
  useWizardCaptionErrors,
  useWizardPublishingAction,
  useWizardTitleErrors,
} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {isUserPremium} from '@Libraries/user.library';
import {getCaptionErrorAccountId, getTitleErrorAccountId, showSocialMediaPublishingUpsellDialog, SocialMediaUpsellEvent} from '@Libraries/social-media';
import {useAppDispatch} from '@/hooks';
import styles from './social-media-mobile-footer.module.scss';
import type {DesignSelection} from '@Components/social-media/social-media.types';

export interface SocialMediaMobileFooterProps {
  nSteps?: number;
  currentStep?: number;
  onNext?: VoidFunction;
  onPrevious?: VoidFunction;
  shouldShowShadow?: boolean;
  currentSelectedDesigns?: DesignSelection[];
}

export function SocialMediaMobileFooter({
  nSteps = 3,
  currentStep = 1,
  onNext = noop,
  onPrevious = noop,
  shouldShowShadow = false,
  currentSelectedDesigns = [],
}: SocialMediaMobileFooterProps): ReactElement<SocialMediaMobileFooterProps> | null {
  const wizardStep = useWizardStep();
  const wizardPost = useSocialMediaWizardPost();
  const isNextDisabled = useNextButtonDisabled();
  const nextButtonText = useNextButtonText();
  const nextButtonIcon = useNextButtonIcon();
  const hideNextButton = useHideNextButton();
  const dispatch = useAppDispatch();
  const publishingAction = useWizardPublishingAction();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const captionErrors = useWizardCaptionErrors();
  const titleErrors = useWizardTitleErrors();
  const currentStepFromRedux = useWizardStep();
  const areCaptionsBeingAutoGenerated = useAreCaptionsBeingAutoGenerated();

  const getFooterType = (): SocialMediaMobileFooterType => {
    if (wizardStep === SocialMediaWizardStep.ADD_DETAILS) {
      if (wizardPost?.publishOn && wizardPost.timeZoneOffset !== null && wizardPost.state === PostState.SCHEDULE_READY) {
        return SocialMediaMobileFooterType.SCHEDULE_STEP;
      }

      return SocialMediaMobileFooterType.PUBLISH_STEP;
    }

    return SocialMediaMobileFooterType.NAVIGATE_STEP;
  };

  const renderBackIcon = (): ReactElement<any> | null => {
    return <Icon onClick={onClickBackIcon} icon="icon icon-arrow-left" size={IconSize.SIZE_ICON_20} isDisabled={currentStep === 1} type={IconType.SECONDARY} shape={IconShape.SQUARE} />;
  };

  const shouldShowPremiumUpselling = (): boolean => {
    return currentStepFromRedux === SocialMediaWizardStep.SELECT_DESIGN && !isUserPremium() && currentSelectedDesigns.length > 1;
  };

  const onClickNextIcon = (): void => {
    if (shouldShowPremiumUpselling()) {
      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_SOCIAL_MEDIA_MULTI_POSTING as string);

      return;
    }

    onNext();
  };

  const onClickBackIcon = (): void => {
    onPrevious();
  };

  const renderNextButton = (): ReactElement<any> | null => {
    if (getFooterType() !== SocialMediaMobileFooterType.NAVIGATE_STEP && wizardStep === SocialMediaWizardStep.ADD_DETAILS) {
      return null;
    }

    let icon = '';

    if (wizardStep === SocialMediaWizardStep.ADD_DETAILS) {
      icon = nextButtonIcon;
    }

    return (
      <Button
        onClick={onClickNextIcon}
        disabled={isNextDisabled || areCaptionsBeingAutoGenerated}
        customClasses={hideNextButton ? '_invisible' : ''}
        type={shouldShowPremiumUpselling() ? Type.PREMIUM : Type.PRIMARY}
        icon={icon}
        iconPlacement={IconPlacement.LEFT}
        text={nextButtonText || window.i18next.t('pmwjs_next')}
        size={Size.SMALL}
      />
    );
  };

  const onMoreOptionsClick = (): void => {
    const footerType = getFooterType();
    const icons = getIconsByFooterType(footerType);
    const optionsText = getOptionsTextByFooterType(footerType);
    const optionsOnClick = getOptionFunctionsByFooterType(footerType);

    const modalProps: SocialMediaWizardMobileFooterModalProps = {icons, optionsText, optionsOnClick};
    openSocialMediaMobileFooterModal(modalProps);
  };

  const onEditClick = (): void => {
    const modalProps: SocialMediaWizardMobileFooterModalProps = {showDateTimePicker: true};
    openSocialMediaMobileFooterModal(modalProps);
  };

  const onButtonClick = (): void => {
    if (getFooterType() === SocialMediaMobileFooterType.SCHEDULE_STEP) {
      if (!isUserPremium()) {
        showSocialMediaPublishingUpsellDialog(SocialMediaUpsellEvent.SCHEDULE);
        return;
      }

      if (wizardPost?.publishOn && wizardPost.timeZoneOffset) {
        if (getCaptionErrorAccountId(accountIdsToPublishTo, captionErrors) !== null || getTitleErrorAccountId(accountIdsToPublishTo, titleErrors) !== null) {
          dispatch(updateTriggerNext());
          return;
        }

        dispatch(updatePublishingAction(SocialPostPublishingAction.SCHEDULE));
        dispatch(updateTriggerNext());
      }

      return;
    }

    if (getFooterType() === SocialMediaMobileFooterType.PUBLISH_STEP) {
      onNext();
      dispatch(setWizardMobileNavView(WizardMobileNavView.CONTENT));
    }
  };

  const renderOptions = (): ReactElement<any> | null => {
    const footerType = getFooterType();

    if (footerType === SocialMediaMobileFooterType.NAVIGATE_STEP || wizardStep !== SocialMediaWizardStep.ADD_DETAILS) {
      return null;
    }

    const isPublishing = footerType === SocialMediaMobileFooterType.PUBLISH_STEP;

    return (
      <div className="_full-height flex-row-justify-between">
        <Icon
          onClick={onMoreOptionsClick}
          icon="icon icon-more-h"
          size={IconSize.SIZE_ICON_20}
          className="spacing-m-r-2"
          type={IconType.PRIMARY}
          shape={IconShape.SQUARE}
          isDisabled={areCaptionsBeingAutoGenerated}
        />
        <Button
          onClick={onButtonClick}
          size={Size.SMALL}
          type={Type.PRIMARY}
          icon={isPublishing ? 'icon icon-publish-arrow' : 'icon icon-calendar'}
          iconPlacement={isPublishing ? IconPlacement.RIGHT : IconPlacement.LEFT}
          text={isPublishing ? window.i18next.t('pmwjs_publish') : window.i18next.t('pmwjs_schedule')}
          disabled={isNextDisabled || areCaptionsBeingAutoGenerated}
        />
      </div>
    );
  };

  const renderProgress = (): ReactElement<any> | null => {
    if (getFooterType() !== SocialMediaMobileFooterType.NAVIGATE_STEP) {
      return null;
    }

    const progressSteps = [];

    for (let i = 0; i < nSteps; i++) {
      progressSteps.push(
        <div id={`circle-${i}`} key={`circle-${i}`} className={`${i !== nSteps - 1 ? 'spacing-m-r-2' : ''} ${styles.circle} ${i < currentStep ? styles.filled : ''}`} />
      );
    }

    return <div className="flex-row-justify-between">{progressSteps}</div>;
  };

  const getCalendarIcon = (): ReactElement<any> => {
    return (
      <Icon
        icon="icon icon-calendar"
        size={IconSize.SIZE_ICON_16}
        type={IconType.FLAT}
        shape={IconShape.SQUARE}
        className="spacing-m-r-2"
        flatIconType={{color: FlatIconColor.PRIMARY, type: FlatIconColorType.DARKER}}
      />
    );
  };

  const getDateString = (dateTimeString: string): string[] => {
    const parts = dateTimeString.split(', ');

    const datePart = `${parts[0]}, ${parts[1]}`;
    const timePart = parts[2];

    return [datePart, timePart];
  };

  const getScheduleContainer = (): null | ReactElement<any> => {
    if (!wizardPost?.publishOn) {
      return null;
    }

    const stringDate = formatLocalDate(wizardPost.publishOn);
    const dateTimeArray = getDateString(stringDate);
    const date = dateTimeArray[0];
    const time = dateTimeArray[1];

    return (
      <div className="flex-column-justify-between">
        <Text val={date} bold size={TextSize.XSMALL} className="content-body-white" />
        <Text val={time} bold size={TextSize.XXSMALL} className="color-extra-light" />
      </div>
    );
  };

  const handleSaveAsDraft = (): void => {
    if (getCaptionErrorAccountId(accountIdsToPublishTo, captionErrors) !== null || getTitleErrorAccountId(accountIdsToPublishTo, titleErrors) !== null) {
      dispatch(updateTriggerNext());
      return;
    }

    doDeleteScheduling();
    dispatch(updatePublishingAction(SocialPostPublishingAction.SAVE));
    dispatch(updateTriggerNext());
  };

  const doDeleteScheduling = (): void => {
    if (wizardPost?.publishOn && wizardPost.timeZoneOffset !== null) {
      const {publishOn: _, timeZoneOffset: __, state: ___, ...unscheduledPost} = wizardPost;
      const draftPost = {...unscheduledPost, state: PostState.DRAFT};
      unschedulePost();
      dispatch(updatePost(draftPost));
    }
  };

  const handlePublishRightNow = (): void => {
    if (getCaptionErrorAccountId(accountIdsToPublishTo, captionErrors) !== null || getTitleErrorAccountId(accountIdsToPublishTo, titleErrors) !== null) {
      dispatch(updateTriggerNext());
      return;
    }

    doDeleteScheduling();

    dispatch(updatePublishingAction(SocialPostPublishingAction.PUBLISH));
    dispatch(updateTriggerNext());
  };

  const getOptionFunctionsByFooterType = (footerType: SocialMediaMobileFooterType): VoidFunction[] => {
    if (footerType === SocialMediaMobileFooterType.NAVIGATE_STEP) {
      return [];
    }

    if (footerType === SocialMediaMobileFooterType.PUBLISH_STEP) {
      return [handleSaveAsDraft, noop];
    }

    if (footerType === SocialMediaMobileFooterType.SCHEDULE_STEP) {
      return [handleSaveAsDraft, handlePublishRightNow];
    }

    return [];
  };

  const unschedulePost = (): void => {
    if (wizardPost === undefined) {
      return;
    }

    void window.PMW.write(window.PMW.util.site_url('socialmedia/unschedulePost'), {idPost: wizardPost.id});
  };

  const onDeleteSchedule = (): void => {
    if (wizardPost?.publishOn && wizardPost.timeZoneOffset !== null) {
      const {publishOn: _, timeZoneOffset: __, state: ___, ...unscheduledPost} = wizardPost;
      const draftPost = {...unscheduledPost, state: PostState.DRAFT};
      unschedulePost();
      dispatch(updatePost(draftPost));
    }
  };

  const getEditIconsGroup = (): ReactElement => {
    return (
      <>
        <Icon
          onClick={() => {
            onDeleteSchedule();
          }}
          icon="icon icon-delete"
          size={IconSize.SIZE_ICON_16}
          type={IconType.DARK_SECONDARY}
          shape={IconShape.SQUARE}
          className="spacing-m-r-2"
        />
        <Icon
          onClick={() => {
            onEditClick();
          }}
          icon="icon icon-pencil"
          size={IconSize.SIZE_ICON_16}
          type={IconType.DARK_SECONDARY}
          shape={IconShape.SQUARE}
          flatIconType={{color: FlatIconColor.PRIMARY, type: FlatIconColorType.DARKER}}
        />
      </>
    );
  };

  if (publishingAction) {
    return null;
  }

  return (
    <div className={`${shouldShowShadow ? styles.bottomShadow : ''} _full-width flex-column-justify-between spacing-p-3 flex-align-items-center ${styles.footerContainer}`}>
      {getFooterType() === SocialMediaMobileFooterType.SCHEDULE_STEP ? (
        <div className={`_full-width spacing-m-b-3 flex-align-items-center flex-row-justify-between spacing-p-1 ${styles.scheduleContainer}`}>
          <div className="flex-h-row flex-align-items-center">
            {getCalendarIcon()} {getScheduleContainer()}
          </div>
          <div className="flex-h-row">{getEditIconsGroup()}</div>
        </div>
      ) : null}
      <div className="_full-width flex-row-justify-between flex-align-items-center">
        {renderBackIcon()} {renderProgress()} {renderNextButton()} {renderOptions()}
      </div>
    </div>
  );
}
